import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Grid,
  LinearProgress,
  MenuItem,
  Paper,
  TablePagination,
  TextField,
  Typography,
} from "@material-ui/core";
import { fade, makeStyles } from "@material-ui/core/styles";
import SearchIcon from "@material-ui/icons/Search";

import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  root: { display: "flex", flexDirection: "column", gap: theme.spacing(1.5) },
  filters: {
    padding: theme.spacing(1.5),
    borderRadius: 18,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: "none",
  },
  card: {
    height: "100%",
    padding: theme.spacing(2),
    borderRadius: 18,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: "none",
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.25),
  },
  cardTitle: { fontWeight: 700 },
  cardPurpose: { color: theme.palette.text.secondary, flex: 1 },
  chips: { display: "flex", flexWrap: "wrap", gap: theme.spacing(0.75) },
  knowledge: {
    padding: theme.spacing(1),
    borderRadius: 10,
    backgroundColor: fade(theme.palette.info.light, 0.12),
  },
  empty: { padding: theme.spacing(6, 2), textAlign: "center" },
  pagination: {
    borderRadius: 16,
    border: `1px solid ${theme.palette.divider}`,
  },
}));

const knowledgeLabel = {
  none: "Não usa base de conhecimento",
  optional: "Base de conhecimento opcional",
  required: "Base de conhecimento obrigatória",
};

const AgentTemplateLibrary = ({ onUse }) => {
  const classes = useStyles();
  const [templates, setTemplates] = useState([]);
  const [categories, setCategories] = useState([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(9);
  const [searchParam, setSearchParam] = useState("");
  const [category, setCategory] = useState("");
  const [loading, setLoading] = useState(false);

  const loadTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/agent-templates", {
        params: {
          searchParam,
          category: category || undefined,
          pageNumber: page + 1,
          pageSize: rowsPerPage,
        },
      });
      setTemplates(data.templates || []);
      setCategories(data.categories || []);
      setCount(data.count || 0);
    } catch (error) {
      toastError(error);
    } finally {
      setLoading(false);
    }
  }, [category, page, rowsPerPage, searchParam]);

  useEffect(() => {
    const timer = setTimeout(loadTemplates, 250);
    return () => clearTimeout(timer);
  }, [loadTemplates]);

  return (
    <div className={classes.root}>
      <Box>
        <Typography variant="h6">Biblioteca de templates</Typography>
      </Box>
      <Paper className={classes.filters}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
              label="Buscar por nome, finalidade ou categoria"
              value={searchParam}
              onChange={(event) => {
                setSearchParam(event.target.value);
                setPage(0);
              }}
              InputProps={{ startAdornment: <SearchIcon color="action" /> }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              select
              fullWidth
              label="Categoria"
              value={category}
              onChange={(event) => {
                setCategory(event.target.value);
                setPage(0);
              }}
            >
              <MenuItem value="">Todas</MenuItem>
              {categories.map((item) => (
                <MenuItem key={item} value={item}>{item}</MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
      </Paper>
      {loading && <LinearProgress />}
      {!loading && templates.length === 0 ? (
        <Paper className={classes.empty}>
          <Typography variant="h6">Nenhum template encontrado</Typography>
          <Typography variant="body2" color="textSecondary">
            Ajuste os filtros para consultar outras opções.
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {templates.map((template) => {
            const version = template.publishedVersion;
            return (
              <Grid item xs={12} md={6} lg={4} key={template.id}>
                <Paper className={classes.card}>
                  <Box>
                    <Typography className={classes.cardTitle}>{version.name}</Typography>
                    <Typography variant="caption" color="textSecondary">
                      {template.slug} · revisão {version.version}
                    </Typography>
                  </Box>
                  <Typography variant="body2" className={classes.cardPurpose}>
                    {version.purpose}
                  </Typography>
                  <div className={classes.chips}>
                    <Chip size="small" color="primary" label={version.category} />
                    <Chip size="small" variant="outlined"
                      label={`${(version.variables || []).length} campos`} />
                  </div>
                  <Typography variant="caption" className={classes.knowledge}>
                    {knowledgeLabel[version.knowledgeRequirement] || "Conhecimento opcional"}
                  </Typography>
                  <Button color="primary" variant="contained" onClick={() => onUse(template)}>
                    Usar este template
                  </Button>
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      )}
      <TablePagination
        component={Paper}
        className={classes.pagination}
        count={count}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={(_event, nextPage) => setPage(nextPage)}
        onRowsPerPageChange={(event) => {
          setRowsPerPage(Number(event.target.value));
          setPage(0);
        }}
        rowsPerPageOptions={[9, 18, 36]}
        labelRowsPerPage="Por página"
      />
    </div>
  );
};

export default AgentTemplateLibrary;
